import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { toast } from "@redotech/redo-web/alert";
import { reactQueryClient } from "@redotech/redo-web/rpc-query/react-query-config";
import { useState } from "react";
import { supportTicketTranscriptKeys } from "../db/collections";
import { catchApiErrors } from "../db/error-handling";
import { invalidateSupportTicketListQueriesAfterDelay } from "../invalidate-support-ticket-list-queries";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";

export interface UndoSupportTicketMergeInput {
  relationshipId?: string;
  sourceSupportTicketId: SupportTicketId;
  destinationSupportTicketId: SupportTicketId;
}

export function useUndoSupportTicketMerge(): {
  pendingMergeKey: string | null;
  undoSupportTicketMerge: (
    input: UndoSupportTicketMergeInput & { mergeKey: string },
  ) => Promise<void>;
} {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const collections = useGlobalSupportTicketCollections();
  const [pendingMergeKey, setPendingMergeKey] = useState<string | null>(null);

  const undoSupportTicketMerge = useHandler(
    async ({
      mergeKey,
      ...input
    }: UndoSupportTicketMergeInput & { mergeKey: string }) => {
      setPendingMergeKey(mergeKey);
      try {
        await effectApi.runPromise(
          catchApiErrors(
            effectApi
              .getClient()
              .supportTicket.undoSupportTicketMerge({ payload: input }),
          ),
        );
        toast("Merge undone", { variant: "success" });
        void reactQueryClient.invalidateQueries({
          queryKey: supportTicketTranscriptKeys.all,
        });
        invalidateSupportTicketListQueriesAfterDelay({
          queryClient: reactQueryClient,
          queryScope: collections.supportTicketsList.queryScope,
        });
      } catch {
        // catchApiErrors owns the user-facing error toast.
      } finally {
        setPendingMergeKey(null);
      }
    },
  );

  return { pendingMergeKey, undoSupportTicketMerge };
}
